import { useState, useEffect, useContext } from "react";
import { FirebaseContext } from "../context/firebaseContext";
import { ISubscription } from "@leedpulse-monorepo/interfaces";

/**
 * Hook to fetch the user's subscription
 * @param {string} subscriptionId - The ID of the subscription (optional, will be taken from context if not provided)
 * @param {boolean} enabled - If false, does not fetch the subscription (default: true)
 * @returns {{ subscription: object|null, loading: boolean, error: string|null }}
 */
export const useSubscription = (subscriptionId: string | null = null, enabled = true) => {
  const { subscriptionId: contextSubscriptionId, getSubscriptionById } = useContext(FirebaseContext);
  
  const [subscription, setSubscription] = useState<ISubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const idToUse = subscriptionId || contextSubscriptionId;

  useEffect(() => {
    if (!enabled || !idToUse) {
      // Synchro avec une source distante : l'etat vient d'un appel reseau, pas du
      // rendu. C'est l'usage d'un effet que la regle autorise ("synchronizing with an
      // external system") ; le setState synchrone est le reset qui precede l'appel.
      // eslint-disable-next-line react/set-state-in-effect
      setLoading(false);
      return;
    }

    const loadSubscription = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getSubscriptionById(idToUse);
        
        if (res.status === "success" && res.doc) {
          setSubscription(res.doc);
        } else {
          setError(res.msg || "Erreur lors du chargement de l'abonnement");
          setSubscription(null);
        }
      } catch (err: any) {
        setError(err.message || "Une erreur est survenue");
        setSubscription(null);
      } finally {
        setLoading(false);
      }
    };

    loadSubscription();
  }, [idToUse, enabled, getSubscriptionById]);

  return { subscription, loading, error };
};

